import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { cardsService } from '../services/cardsService';
import { Plus, CreditCard, Eye, MousePointerClick, QrCode, Inbox } from 'lucide-react';

const Dashboard = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const data = await cardsService.getMyCards();
      // Ensure data is always an array
      setCards(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Erreur lors du chargement des cartes');
      setCards([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (cardId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette carte ?')) {
      try {
        await cardsService.deleteCard(cardId);
        setCards(cards.filter((c) => c.id !== cardId));
      } catch (err) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-[#2B4A6F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        {/* Header - Mobile Responsive */}
        <div className="mb-6 md:mb-8">
          {/* Title */}
          <div className="mb-4 md:mb-6">
            <h1 className="text-white text-2xl md:text-3xl font-bold mb-1 md:mb-2">Mes Cartes</h1>
            <p className="text-gray-400 text-sm md:text-base">Gérez vos cartes de visite digitales</p>
          </div>
          
          {/* Actions - Mobile Stack */}
          <div className="flex flex-col sm:flex-row gap-3 md:hidden">
            <Link
              to="/cards/new"
              className="btn-primary flex items-center justify-center space-x-2 px-6 py-3 rounded-xl font-semibold text-base touch-manipulation active:scale-95 transition-transform"
            >
              <Plus size={20} />
              <span>Nouvelle carte</span>
            </Link>
            <Link
              to="/shared-contacts"
              className="flex items-center justify-center space-x-2 px-5 py-3 rounded-xl font-semibold bg-gradient-to-br from-[#0A0A0A] to-[#151515] text-white border border-gray-800/50 hover:border-[#2B4A6F]/50 transition-all touch-manipulation active:scale-95"
            >
              <Inbox size={20} />
              <span>Contacts reçus</span>
            </Link>
          </div>
          
          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-3">
            <Link
              to="/shared-contacts"
              className="flex items-center space-x-2 px-5 py-3 rounded-xl font-semibold bg-gradient-to-br from-[#0A0A0A] to-[#151515] text-white border border-gray-800/50 hover:border-[#2B4A6F]/50 transition-all"
            >
              <Inbox size={20} />
              <span>Contacts reçus</span>
            </Link>
            <Link
              to="/cards/new"
              className="btn-primary flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold"
            >
              <Plus size={20} />
              <span>Nouvelle carte</span>
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Cards Grid */}
        {cards.length === 0 ? (
          <div className="text-center py-16 glass rounded-3xl">
            <CreditCard size={64} className="text-gray-600 mx-auto mb-4" />
            <h3 className="text-white text-xl font-semibold heading-display mb-2">Aucune carte</h3>
            <p className="text-gray-400 mb-6">Créez votre première carte de visite digitale</p>
            <Link
              to="/cards/new"
              className="btn-primary inline-flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold"
            >
              <Plus size={20} />
              <span>Créer ma carte</span>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {cards.map((card) => (
              <div
                key={card.id}
                className="glass p-6 rounded-3xl hover:bg-white/10 transition-all group"
              >
                {/* Card Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">{card.full_name}</h3>
                    <p className="text-gray-400 text-sm">{card.job_title}</p>
                    <p className="text-gray-500 text-xs mt-1">{card.company}</p>
                  </div>
                  {card.qr_code_base64 && (
                    <div className="w-16 h-16 bg-white rounded-lg p-1 flex-shrink-0">
                      <img src={card.qr_code_base64} alt="QR Code" className="w-full h-full" />
                    </div>
                  )}
                </div>

                {/* Card Info */}
                <div className="space-y-2 mb-4">
                  <p className="text-gray-400 text-sm truncate">{card.email}</p>
                  {card.phone && <p className="text-gray-400 text-sm">{card.phone}</p>}
                </div>

                {/* Card Actions */}
                <div className="flex items-center space-x-2 pt-4 border-t border-white/10">
                  <Link
                    to={`/cards/${card.id}`}
                    className="flex-1 text-center btn-primary py-2.5 rounded-xl text-sm font-medium touch-manipulation active:scale-95 transition-transform"
                  >
                    Voir
                  </Link>
                  <Link
                    to={`/cards/${card.id}/edit`}
                    className="flex-1 text-center glass hover:bg-white/10 text-white py-2.5 rounded-xl text-sm font-medium transition-all touch-manipulation active:scale-95"
                  >
                    Modifier
                  </Link>
                  <button
                    onClick={() => handleDelete(card.id)}
                    className="px-4 py-2.5 glass hover:bg-red-500/10 text-red-400 rounded-xl text-sm font-medium transition-all touch-manipulation active:scale-95"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
